import { VStack, HStack, Text } from 'native-base';

import * as Yup from 'yup';
import { Formik, ErrorMessage } from 'formik';

import { BugAntIcon, MegaphoneIcon } from '@heroicons/react/24/solid';
import { CustomCheckbox, PrimaryButton } from '../../';
import { TextView } from '../../input/text-view/text-view';
import { InputType, Status, FeedbackType } from '@breakroom-nx/helpers';
import { theme } from '@breakroom-nx/assets';
import {
  AppFeedbackType,
  Feedback,
  ImplementationState,
} from '@breakroom-nx/classes';
import {
  openModal,
  toggleForm,
  useAppDispatch,
  useGetCurrentUserQuery,
  useCreateFeedbackMutation,
} from '@breakroom-nx/redux';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { useState } from 'react';

const FeedbackScheme = Yup.object().shape({
  content: Yup.string()
    .required('Please provide a desciption')
    .max(250, 'Sorry, a max of only 250 characters'),
});

interface FeedbackFormValues {
  content: string;
}

export function FeedbackForm() {
  const dispatch = useAppDispatch();
  const [feedbackType, setFeedbackType] = useState<FeedbackType | undefined>(
    undefined
  );
  const { data: currentUser } = useGetCurrentUserQuery();
  const [sendFeedback, sendFeedbackState] = useCreateFeedbackMutation();
  const initialValues: FeedbackFormValues = { content: '' };

  const feedbackChoices = [
    {
      title: 'Suggestion',
      value: FeedbackType.Suggestion,
      icon: (
        <MegaphoneIcon
          style={{
            height: '20px',
            width: '20px',
            color: theme.colors.primary[400],
          }}
        />
      ),
    },
    {
      title: 'Bug',
      value: FeedbackType.Bug,
      icon: (
        <BugAntIcon
          style={{
            height: '20px',
            width: '20px',
            color: theme.colors.primary[400],
          }}
        />
      ),
    },
  ];

  const selectFeedbackChoice = (choiceValue: FeedbackType) => {
    // console.log(`Choice: ${choiceValue} | Feedback Type: ${feedbackType}`);
    if (choiceValue == feedbackType) {
      setFeedbackType(undefined);
    } else {
      setFeedbackType(choiceValue);
    }
  };

  const renderFeedbackCheckboxes = () => {
    return feedbackChoices.map((choice) => (
      <CustomCheckbox
        key={choice.title}
        title={choice.title}
        checked={feedbackType === choice.value}
        onCheckboxSelect={() => selectFeedbackChoice(choice.value)}
        startIcon={choice.icon}
        otherSelected={!!feedbackType && feedbackType !== choice.value}
      />
    ));
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={FeedbackScheme}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        const newFeedback = new Feedback({
          creatorUID: currentUser?.uid,
          content: values.content,
          implementationState:
            feedbackType === FeedbackType.Bug
              ? ImplementationState.Bug
              : ImplementationState.Suggested,
        });

        if (!feedbackType) {
          // Optionally show an error or do nothing
        } else {
          sendFeedback({
            user: currentUser,
            feedback: newFeedback,
            type: feedbackType,
          })
            .unwrap()
            .then((data) => {
              resetForm();
              dispatch(toggleForm());
              dispatch(
                openModal({
                  modalType: 'Alert',
                  modalProps: {
                    status: Status.success,
                    title: 'Feedback Received!',
                  },
                })
              );
            })
            .catch((error) => {
              dispatch(
                openModal({
                  modalType: 'Alert',
                  modalProps: {
                    status: Status.error,
                    title: 'Error submitting feedback. Please try again.',
                  },
                })
              );
            });
        }
      }}
    >
      {({ values, isSubmitting, handleChange, handleBlur, handleSubmit }) => (
        <VStack
          space={5}
          rounded={'lg'}
          p={5}
          mx={8}
          mb={8}
          bg={theme.colors.surface[200]}
        >
          <Text
            fontFamily={'heading'}
            fontSize={23}
            color={'white'}
            fontWeight={600}
          >
            {'Submit Feedback'}
          </Text>
          <HStack space={2}>{renderFeedbackCheckboxes()}</HStack>
          <TextView
            disabled={sendFeedbackState.status === QueryStatus.pending}
            type={InputType.Text}
            value={values.content}
            onChangeText={handleChange('content')}
            onBlur={handleBlur('content')}
            isRequired={true}
            height={150}
            placeholderTextColor={'surface.600'}
            inactiveTextColor={'surface.600'}
            placeholder={
              feedbackType == undefined
                ? 'Please select the type of feedback you want to submit'
                : feedbackType === FeedbackType.Suggestion
                ? 'Please describe what we can do better or a feature you would like to see'
                : 'Please describe the bug in as much detail as you can'
            }
          />
          <Text variant={'error'}>
            <ErrorMessage name="content" />
          </Text>
          <PrimaryButton
            disabled={
              sendFeedbackState.status === QueryStatus.pending ||
              !currentUser ||
              !feedbackType
            }
            isLoading={sendFeedbackState.status === QueryStatus.pending}
            text="Submit"
            onPress={handleSubmit}
          />
        </VStack>
      )}
    </Formik>
  );
}

export default FeedbackForm;
